import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEditor } from '../contexts/EditorContext';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { useProjectSync } from '../hooks/useProjectSync';
import { supabase } from '../lib/supabase';
import AppHeader from '../components/AppHeader';
import CanvasSizeSelector from '../components/CanvasSizeSelector';
import AddTextElement from '../components/AddTextElement';
import AddImageElement from '../components/AddImageElement';
import AddShapeElement from '../components/AddShapeElement';
import { useAlert } from '../hooks/useAlert';
import ConfirmDialog from '../components/ConfirmDialog';
import Toolbar from '../components/Toolbar';
import ElementSettingsPanel from '../components/ElementSettingsPanel';
import ElementsList from '../components/ElementsList';
import MobileBlocker from '../components/MobileBlocker';
import { EditorElement } from '../types/elements';

// Safe DOM element removal helper to prevent React removeChild errors
const safeRemoveElement = (element: Node | null | undefined): void => {
  if (!element) return;
  
  try {
    // Use remove() method if available (safer and more modern)
    if (element instanceof Element && typeof element.remove === 'function') {
      element.remove();
      return;
    }
    
    // Fallback to removeChild with proper checks
    const parent = element.parentNode;
    if (parent) {
      parent.removeChild(element);
    }
  } catch (error) {
    // Silently ignore removal errors - element might already be removed by React
    // This prevents the "node to be removed is not a child" error
  }
};

export default function EditorPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, dir } = useLanguage();
  const { showAlert, AlertComponent } = useAlert();
  const { projectId, projectData, loading: projectLoading, saving, saveSuccess, hasUnsavedChanges, accessDenied, saveProject, markUnsaved } = useProjectSync();
  const [showExitConfirm, setShowExitConfirm] = React.useState(false);
  const [editingProjectName, setEditingProjectName] = React.useState<string | null>(null);

  // Initialize canvas size from project data or default
  const getInitialSize = () => {
    if (projectData?.editor_data) {
      return [projectData.editor_data.canvasWidth || 800, projectData.editor_data.canvasHeight || 600];
    }
    return [800, 600];
  };
  const [canvasWidth, setCanvasWidth] = React.useState(getInitialSize()[0]);
  const [canvasHeight, setCanvasHeight] = React.useState(getInitialSize()[1]);
  
  // Update canvas size when project loads
  React.useEffect(() => {
    if (projectData?.editor_data) {
      setCanvasWidth(projectData.editor_data.canvasWidth || 800);
      setCanvasHeight(projectData.editor_data.canvasHeight || 600);
    }
  }, [projectData]);
  // حفظ الحجم عند التغيير
  React.useEffect(() => {
    localStorage.setItem('editor-canvas-size', JSON.stringify({ w: canvasWidth, h: canvasHeight }));
  }, [canvasWidth, canvasHeight]);

  // حفظ واسترجاع العناصر
  const { elements, setElements, updateElement } = useEditor();
  
  // Load elements from project data
  React.useEffect(() => {
    if (projectData?.editor_data?.elements && Array.isArray(projectData.editor_data.elements)) {
      const loadedElements = projectData.editor_data.elements;
      setElements(loadedElements);
      
      // تهيئة History بالعناصر المحملة
      setHistory([JSON.parse(JSON.stringify(loadedElements))]);
      setHistoryIndex(0);
    }
    // eslint-disable-next-line
  }, [projectData]);

  // Mark as unsaved when elements change (after initial load)
  const initialLoadRef = React.useRef(true);
  const historyTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  
  React.useEffect(() => {
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      return;
    }
    
    markUnsaved();
    
    // Debounce: احفظ في History فقط بعد توقف التعديلات لمدة ثانية
    if (!isUndoRedoAction.current) {
      if (historyTimerRef.current) {
        clearTimeout(historyTimerRef.current);
      }
      
      historyTimerRef.current = setTimeout(() => {
        setHistoryIndex(prevIndex => {
          setHistory(prevHistory => {
            const newHistory = prevHistory.slice(0, prevIndex + 1);
            newHistory.push(JSON.parse(JSON.stringify(elements)));
            
            // احتفظ بآخر 50 حالة فقط
            if (newHistory.length > 50) {
              newHistory.shift();
              return newHistory;
            }
            return newHistory;
          });
          return Math.min(prevIndex + 1, 49);
        });
      }, 1000); // ثانية واحدة بعد التوقف
    }
    isUndoRedoAction.current = false;
    
    return () => {
      if (historyTimerRef.current) {
        clearTimeout(historyTimerRef.current);
      }
    };
  }, [elements, markUnsaved]);
  
  // تحديث العناصر القديمة التي ليس لديها fontFamily أو autoFitFont مرة واحدة فقط
  const hasRunFontFix = React.useRef(false);
  React.useEffect(() => {
    if (hasRunFontFix.current || elements.length === 0) return;
    
    const needsUpdate = elements.some((el: any) => 
      el.type === 'text' && (!el.fontFamily || el.autoFitFont === undefined)
    );
    if (needsUpdate) {
      const updatedElements = elements.map((el: any) => {
        if (el.type === 'text') {
          const updates: any = {};
          if (!el.fontFamily) {
            console.log('Updating element', el.id, 'with default font');
            updates.fontFamily = 'Cairo, sans-serif';
          }
          if (el.autoFitFont === undefined) {
            console.log('Enabling autoFitFont for element', el.id);
            updates.autoFitFont = true;
          }
          return Object.keys(updates).length > 0 ? { ...el, ...updates } : el;
        }
        return el;
      });
      setElements(updatedElements);
      hasRunFontFix.current = true;
    }
    // eslint-disable-next-line
  }, [elements.length]);
  React.useEffect(() => {
    localStorage.setItem('editor-elements', JSON.stringify(elements));
  }, [elements]);

  // إعدادات المشروع
  // projectId now comes from useProjectSync hook (removed old state)

  const MAX_PREVIEW = 700;
  const [zoomLevel, setZoomLevel] = React.useState(1);
  const baseScale = Math.min(1, MAX_PREVIEW / Math.max(canvasWidth, canvasHeight));
  const scale = baseScale * zoomLevel;
  const [activeTool, setActiveTool] = React.useState<'text' | 'image' | 'shape' | null>(null);
  const [selectedShapeType, setSelectedShapeType] = React.useState<'rectangle' | 'circle' | 'triangle' | null>(null);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const selectedElement = elements.find((el: any) => el.id === selectedId) || null;
  const draggingIdRef = React.useRef<string | null>(null);
  const [draggingId, setDraggingId] = React.useState<string | null>(null);
  const [editingTextId, setEditingTextId] = React.useState<string | null>(null);
  const editingTextRef = React.useRef<HTMLDivElement | null>(null);
  const dragOffset = React.useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const isDraggingRef = React.useRef(false);
  const [isDragging, setIsDragging] = React.useState(false);
  // Smart Guides (خطوط المحاذاة)
  const [showCenterGuideX, setShowCenterGuideX] = React.useState(false);
  const [showCenterGuideY, setShowCenterGuideY] = React.useState(false);
  // Resize handles
  const [resizingId, setResizingId] = React.useState<string | null>(null);
  const resizingIdRef = React.useRef<string | null>(null);
  const resizeHandleRef = React.useRef<string | null>(null);
  const [resizeHandle, setResizeHandle] = React.useState<string | null>(null);
  const resizeStartData = React.useRef<{ x: number; y: number; width: number; height: number; elX: number; elY: number }>({ x: 0, y: 0, width: 0, height: 0, elX: 0, elY: 0 });
  
  // Undo/Redo System
  const [history, setHistory] = React.useState<EditorElement[][]>([]);
  const [historyIndex, setHistoryIndex] = React.useState(-1);
  const isUndoRedoAction = React.useRef(false);
  
  // لا حاجة لدالة opacity - سنستخدم overlay masks بدلاً من ذلك
  const [autoMoveId, setAutoMoveId] = React.useState<string | null>(null);
  const autoMoveTarget = React.useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const autoMoveRaf = React.useRef<number | null>(null);
  const dragHoldTimer = React.useRef<NodeJS.Timeout | null>(null);
  const dragStartPoint = React.useRef<{x: number, y: number} | null>(null);

  // 1. State لتخزين حجم الخط المحسوب لكل عنصر نصي
  const [autoFontSizes, setAutoFontSizes] = React.useState<{ [id: string]: number }>({});
  
  // Mobile check state
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 1024);
  
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 2. useLayoutEffect لحساب حجم الخط عند الحاجة - محسّن للتصغير التلقائي
  React.useLayoutEffect(() => {
    const newSizes: { [id: string]: number } = {};
    elements.forEach((el: any) => {
      if (el.type === 'text' && el.width && el.height) {
        // أنشئ div مخفي لقياس النص
        const measureDiv = document.createElement('div');
        measureDiv.style.position = 'absolute';
        measureDiv.style.visibility = 'hidden';
        measureDiv.style.width = (el.width * scale) + 'px';
        measureDiv.style.height = (el.height * scale) + 'px';
        measureDiv.style.fontFamily = el.fontFamily || 'Cairo, sans-serif';
        measureDiv.style.fontWeight = el.fontWeight ? String(el.fontWeight) : '400';
        measureDiv.style.letterSpacing = (el.letterSpacing || 0) * scale + 'px';
        measureDiv.style.lineHeight = (el.lineHeight || 1.5).toString();
        measureDiv.style.textTransform = el.textTransform || 'none';
        measureDiv.style.whiteSpace = 'pre-wrap';
        measureDiv.style.wordBreak = 'break-word';
        measureDiv.style.padding = '4px 10px';
        measureDiv.style.boxSizing = 'border-box';
        measureDiv.style.textAlign = el.textAlign || 'right';
        measureDiv.style.direction = el.direction || 'rtl';
        
        let testFontSize = el.fontSize * scale;
        let fits = false;
        
        // تحقق أولاً إذا كان النص يتسع بالحجم الحالي
        measureDiv.style.fontSize = testFontSize + 'px';
        measureDiv.innerText = el.text;
        
        try {
          document.body.appendChild(measureDiv);
          fits = measureDiv.scrollHeight <= measureDiv.offsetHeight;
          safeRemoveElement(measureDiv);
        } catch (error) {
          console.warn('Error in initial text measurement:', error);
          safeRemoveElement(measureDiv);
        }
        
        // إذا كان autoFitFont مفعل أو النص يتجاوز الحدود من الأسفل، صغر الخط
        if (el.autoFitFont || !fits) {
          while (testFontSize >= 8 && !fits) {
            testFontSize -= 0.5;
            measureDiv.style.fontSize = testFontSize + 'px';
            measureDiv.innerText = el.text;
            
            try {
              document.body.appendChild(measureDiv);
              fits = measureDiv.scrollHeight <= measureDiv.offsetHeight;
              safeRemoveElement(measureDiv);
            } catch (error) {
              console.warn('Error in font size reduction measurement:', error);
              safeRemoveElement(measureDiv);
              break; // توقف عن التكرار في حالة الخطأ
            }
          }
          newSizes[el.id] = testFontSize;
        } else {
          // إذا كان يتسع، استخدم الحجم الأصلي
          newSizes[el.id] = testFontSize;
        }
      }
    });
    setAutoFontSizes(newSizes);
    // eslint-disable-next-line
  }, [elements, scale]);

  const handleDragStart = (el: any, e: React.PointerEvent) => {
    if (activeTool) return;
    if (e.button !== 0) return;
    setDraggingId(el.id);
    setSelectedId(el.id);
    isDraggingRef.current = false;
    setIsDragging(true);
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    dragOffset.current = {
      x: (e.clientX - rect.left) / scale,
      y: (e.clientY - rect.top) / scale,
    };
    document.addEventListener('pointermove', handleDragMove as any);
    document.addEventListener('pointerup', handleDragEnd as any);
  };

  const handleDragMove = (e: PointerEvent) => {
    const currentDraggingId = draggingIdRef.current;
    if (!currentDraggingId) return;
    isDraggingRef.current = true;
    const canvasRect = document.getElementById('main-canvas-area')?.getBoundingClientRect();
    if (!canvasRect) return;
    
    // احسب الموضع الجديد
    let x = Math.round((e.clientX - canvasRect.left) / scale - dragOffset.current.x);
    let y = Math.round((e.clientY - canvasRect.top) / scale - dragOffset.current.y);
    
    // احصل على أبعاد العنصر
    const el = elements.find((el: any) => el.id === currentDraggingId);
    if (!el) return;
    const elWidth = el.width || 100;
    const elHeight = el.height || 50;
    
    // احسب مركز العنصر
    const elCenterX = x + elWidth / 2;
    const elCenterY = y + elHeight / 2;
    
    // احسب مركز الكانفاس
    const canvasCenterX = canvasWidth / 2;
    const canvasCenterY = canvasHeight / 2;
    
    // مسافة الالتصاق (snap distance)
    const snapDistance = 8; // 8 pixels
    
    // Smart Guide X (عمودي)
    if (Math.abs(elCenterX - canvasCenterX) < snapDistance) {
      x = canvasCenterX - elWidth / 2; // التصق بالمركز
      setShowCenterGuideX(true);
    } else {
      setShowCenterGuideX(false);
    }
    
    // Smart Guide Y (أفقي)
    if (Math.abs(elCenterY - canvasCenterY) < snapDistance) {
      y = canvasCenterY - elHeight / 2; // التصق بالمركز
      setShowCenterGuideY(true);
    } else {
      setShowCenterGuideY(false);
    }
    
    // السماح بالخروج من الحدود - لا توجد قيود!
    updateElement(currentDraggingId, { x, y });
  };

  const handleDragEnd = (e?: PointerEvent) => {
    draggingIdRef.current = null;
    setDraggingId(null);
    setIsDragging(false);
    setShowCenterGuideX(false); // إخفاء الخطوط
    setShowCenterGuideY(false);
    setTimeout(() => { isDraggingRef.current = false; }, 0);
    
    // إزالة Event Listeners بأمان
    try {
      document.removeEventListener('pointermove', handleDragMove as any);
      document.removeEventListener('pointerup', handleDragEnd as any);
    } catch (error) {
      console.warn('Error removing drag event listeners:', error);
    }
  };

  // دوال Undo/Redo
  const handleUndo = () => {
    if (historyIndex > 0) {
      isUndoRedoAction.current = true;
      setHistoryIndex(historyIndex - 1);
      setElements(JSON.parse(JSON.stringify(history[historyIndex - 1])));
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      isUndoRedoAction.current = true;
      setHistoryIndex(historyIndex + 1);
      setElements(JSON.parse(JSON.stringify(history[historyIndex + 1])));
    }
  };

  const handleDeleteSelected = () => {
    if (!selectedId) return;
    setElements(elements.filter((el: any) => el.id !== selectedId));
    setSelectedId(null);
  };

  // دالة لبدء تحرير النص عند Double Click
  const handleDoubleClickText = (el: any, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setEditingTextId(el.id);
    setSelectedId(el.id);
    // تركيز على العنصر بعد render
    setTimeout(() => {
      if (editingTextRef.current) {
        editingTextRef.current.focus();
        // اختر كل النص
        const range = document.createRange();
        range.selectNodeContents(editingTextRef.current);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }, 0);
  };

  // دالة لحفظ النص المحرر
  const handleSaveEditedText = (el: any) => {
    if (!editingTextRef.current) return;
    const newText = editingTextRef.current.innerText;
    updateElement(el.id, { text: newText });
    setEditingTextId(null);
  };

  // إغلاق التحرير عند الضغط خارج النص
  const handleBlurEditedText = (el: any) => {
    handleSaveEditedText(el);
  };

  // تحريك النص بالأسهم + اختصارات لوحة المفاتيح
  React.useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      // السماح بتحرير النص بدون تدخل عند التحرير
      if (editingTextId) return;
      
      // Ctrl/Cmd + S للحفظ
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (projectData?.name) {
          await saveProject(elements, canvasWidth, canvasHeight, projectData.name);
        }
        return;
      }
      
      // Ctrl/Cmd + Z للتراجع (Undo)
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key === 'z') {
        e.preventDefault();
        handleUndo();
        return;
      }
      
      // Ctrl/Cmd + Shift + Z للإعادة (Redo)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z') {
        e.preventDefault();
        handleRedo();
        return;
      }
      
      // تم تعطيل حذف الطبقات بزر Delete/Backspace لتجنب الحذف أثناء تحرير النص
      // يمكن حذف الطبقات من قائمة الطبقات فقط
      
      if (!selectedId) return;
      const el = elements.find((el: any) => el.id === selectedId) as any;
      if (!el || el.locked) return;
      let dx = 0, dy = 0;
      const step = e.shiftKey ? 10 : 1;
      if (e.key === 'ArrowUp') dy = -step;
      if (e.key === 'ArrowDown') dy = step;
      if (e.key === 'ArrowLeft') dx = -step;
      if (e.key === 'ArrowRight') dx = step;
      if (dx !== 0 || dy !== 0) {
        e.preventDefault();
        // السماح بالخروج من الحدود
        updateElement(el.id, {
          x: (el.x || 0) + dx,
          y: (el.y || 0) + dy,
        });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedId, elements, updateElement, canvasWidth, canvasHeight, saveProject, projectData, history, historyIndex, editingTextId]);

  const handleAutoMoveStart = (el: any, e: React.PointerEvent) => {
    if (activeTool) return;
    if (e.button !== 0) return;
    setAutoMoveId(el.id);
    setSelectedId(el.id);
    const canvasRect = document.getElementById('main-canvas-area')?.getBoundingClientRect();
    if (!canvasRect) return;
    autoMoveTarget.current = {
      x: (e.clientX - canvasRect.left) / scale,
      y: (e.clientY - canvasRect.top) / scale,
    };
    document.addEventListener('pointermove', handleAutoMovePointerMove as any);
    document.addEventListener('pointerup', handleAutoMoveEnd as any);
    autoMoveLoop(el.id);
  };

  const handleAutoMovePointerMove = (e: PointerEvent) => {
    const canvasRect = document.getElementById('main-canvas-area')?.getBoundingClientRect();
    if (!canvasRect) return;
    autoMoveTarget.current = {
      x: (e.clientX - canvasRect.left) / scale,
      y: (e.clientY - canvasRect.top) / scale,
    };
  };

  const handleAutoMoveEnd = () => {
    setAutoMoveId(null);
    if (autoMoveRaf.current) cancelAnimationFrame(autoMoveRaf.current);
    
    // إزالة Event Listeners بأمان
    try {
      document.removeEventListener('pointermove', handleAutoMovePointerMove as any);
      document.removeEventListener('pointerup', handleAutoMoveEnd as any);
    } catch (error) {
      console.warn('Error removing auto-move event listeners:', error);
    }
  };

  const autoMoveLoop = (id: string) => {
    if (!autoMoveId || autoMoveId !== id) return;
    const el = elements.find((el: any) => el.id === id);
    if (!el) return;
    const speed = 8;
    const dx = autoMoveTarget.current.x - (el.x + (el.width ? el.width/2 : 0));
    const dy = autoMoveTarget.current.y - (el.y + (el.height ? el.height/2 : 0));
    const dist = Math.sqrt(dx*dx + dy*dy);
    if (dist > 1) {
      const moveX = dx / dist * Math.min(speed, dist);
      const moveY = dy / dist * Math.min(speed, dist);
      let newX = (el.x || 0) + moveX;
      let newY = (el.y || 0) + moveY;
      // السماح بالخروج من الحدود
      updateElement(id, { x: newX, y: newY });
      autoMoveRaf.current = requestAnimationFrame(() => autoMoveLoop(id));
    } else {
      autoMoveRaf.current = requestAnimationFrame(() => autoMoveLoop(id));
    }
  };

  const handlePointerDownOnElement = (el: any, e: React.PointerEvent) => {
    if (activeTool) return;
    if (e.button !== 0) return;
    if (el.locked) return;
    if (resizingId) return; // منع السحب أثناء Resize
    if (editingTextId === el.id) return; // منع السحب أثناء التحرير
    
    e.stopPropagation();
    e.preventDefault(); // منع التحديد الافتراضي للنص
    
    // اختر العنصر مباشرة
    setSelectedId(el.id);
    
    // ابدأ السحب فوراً بدون تأخير
    const elemDiv = document.querySelector(`[data-el-id='${el.id}']`) as HTMLElement;
    if (elemDiv) {
      const rect = elemDiv.getBoundingClientRect();
      dragOffset.current = {
        x: (e.clientX - rect.left) / scale,
        y: (e.clientY - rect.top) / scale,
      };
    }
    
    // استخدم ref للقيمة الفورية
    draggingIdRef.current = el.id;
    setDraggingId(el.id);
    isDraggingRef.current = true;
    setIsDragging(true);
    
    document.addEventListener('pointermove', handleDragMove as any);
    document.addEventListener('pointerup', handleDragEnd as any);
  };

  const handlePointerMoveOnElement = (e: PointerEvent) => {
    // لم تعد هذه الدالة مطلوبة - السحب يبدأ فوراً
  };

  const handlePointerUpOnElement = (e: PointerEvent) => {
    if (dragHoldTimer.current) clearTimeout(dragHoldTimer.current);
    dragHoldTimer.current = null;
    dragStartPoint.current = null;
    
    // إزالة Event Listeners بأمان
    try {
      document.removeEventListener('pointermove', handlePointerMoveOnElement as any);
      document.removeEventListener('pointerup', handlePointerUpOnElement as any);
    } catch (error) {
      console.warn('Error removing pointer event listeners:', error);
    }
  };

  // Resize handlers
  const handleResizeStart = (el: any, handle: string, e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    resizingIdRef.current = el.id;
    resizeHandleRef.current = handle;
    setResizingId(el.id);
    setResizeHandle(handle);
    setSelectedId(el.id);
    
    // حفظ البيانات الأولية
    resizeStartData.current = {
      x: e.clientX,
      y: e.clientY,
      width: el.width,
      height: el.height,
      elX: el.x,
      elY: el.y,
    };
    
    document.addEventListener('pointermove', handleResizeMove as any);
    document.addEventListener('pointerup', handleResizeEnd as any);
  };

  const handleResizeMove = (e: PointerEvent) => {
    const currentResizingId = resizingIdRef.current;
    const currentHandle = resizeHandleRef.current;
    if (!currentResizingId || !currentHandle) return;
    
    const el = elements.find((el: any) => el.id === currentResizingId);
    if (!el) return;
    
    const dx = (e.clientX - resizeStartData.current.x) / scale;
    const dy = (e.clientY - resizeStartData.current.y) / scale;
    
    let newWidth = resizeStartData.current.width;
    let newHeight = resizeStartData.current.height;
    let newX = resizeStartData.current.elX;
    let newY = resizeStartData.current.elY;
    
    // تحقق إذا كانت نقطة زاوية (corner)
    const isCorner = currentHandle.length === 2; // nw, ne, se, sw
    
    // احسب نسبة العرض إلى الارتفاع الأصلية
    const aspectRatio = resizeStartData.current.width / resizeStartData.current.height;
    
    // الزوايا: تحافظ على Aspect Ratio
    if (isCorner) {
      // استخدم التغيير الأكبر للحفاظ على النسبة
      const avgChange = (Math.abs(dx) + Math.abs(dy)) / 2;
      
      if (currentHandle === 'se') { // جنوب شرق (bottom-right)
        const change = Math.max(dx, dy / aspectRatio);
        newWidth = Math.max(50, resizeStartData.current.width + change);
        newHeight = newWidth / aspectRatio;
        // قيود الحدود
        if (newX + newWidth > canvasWidth) {
          newWidth = canvasWidth - newX;
          newHeight = newWidth / aspectRatio;
        }
        if (newY + newHeight > canvasHeight) {
          newHeight = canvasHeight - newY;
          newWidth = newHeight * aspectRatio;
        }
      } 
      else if (currentHandle === 'sw') { // جنوب غرب (bottom-left)
        const change = Math.max(-dx, dy / aspectRatio);
        newWidth = Math.max(50, resizeStartData.current.width + change);
        newHeight = newWidth / aspectRatio;
        newX = resizeStartData.current.elX + resizeStartData.current.width - newWidth;
        // قيود الحدود
        if (newX < 0) {
          newX = 0;
          newWidth = resizeStartData.current.elX + resizeStartData.current.width;
          newHeight = newWidth / aspectRatio;
        }
        if (newY + newHeight > canvasHeight) {
          newHeight = canvasHeight - newY;
          newWidth = newHeight * aspectRatio;
          newX = resizeStartData.current.elX + resizeStartData.current.width - newWidth;
        }
      }
      else if (currentHandle === 'ne') { // شمال شرق (top-right)
        const change = Math.max(dx, -dy / aspectRatio);
        newWidth = Math.max(50, resizeStartData.current.width + change);
        newHeight = newWidth / aspectRatio;
        newY = resizeStartData.current.elY + resizeStartData.current.height - newHeight;
        // قيود الحدود
        if (newX + newWidth > canvasWidth) {
          newWidth = canvasWidth - newX;
          newHeight = newWidth / aspectRatio;
          newY = resizeStartData.current.elY + resizeStartData.current.height - newHeight;
        }
        if (newY < 0) {
          newY = 0;
          newHeight = resizeStartData.current.elY + resizeStartData.current.height;
          newWidth = newHeight * aspectRatio;
        }
      }
      else if (currentHandle === 'nw') { // شمال غرب (top-left)
        const change = Math.max(-dx, -dy / aspectRatio);
        newWidth = Math.max(50, resizeStartData.current.width + change);
        newHeight = newWidth / aspectRatio;
        newX = resizeStartData.current.elX + resizeStartData.current.width - newWidth;
        newY = resizeStartData.current.elY + resizeStartData.current.height - newHeight;
        // قيود الحدود
        if (newX < 0) {
          newX = 0;
          newWidth = resizeStartData.current.elX + resizeStartData.current.width;
          newHeight = newWidth / aspectRatio;
          newY = resizeStartData.current.elY + resizeStartData.current.height - newHeight;
        }
        if (newY < 0) {
          newY = 0;
          newHeight = resizeStartData.current.elY + resizeStartData.current.height;
          newWidth = newHeight * aspectRatio;
          newX = resizeStartData.current.elX + resizeStartData.current.width - newWidth;
        }
      }
    }
    // الأضلاع: تغيير حر بدون Aspect Ratio
    else {
      if (currentHandle === 'e') { // right
        newWidth = Math.max(50, resizeStartData.current.width + dx);
      }
      if (currentHandle === 'w') { // left
        newWidth = Math.max(50, resizeStartData.current.width - dx);
        newX = resizeStartData.current.elX + (resizeStartData.current.width - newWidth);
      }
      if (currentHandle === 's') { // bottom
        newHeight = Math.max(50, resizeStartData.current.height + dy);
      }
      if (currentHandle === 'n') { // top
        newHeight = Math.max(50, resizeStartData.current.height - dy);
        newY = resizeStartData.current.elY + (resizeStartData.current.height - newHeight);
      }
    }
    
    updateElement(currentResizingId, { 
      width: Math.round(newWidth), 
      height: Math.round(newHeight),
      x: Math.round(newX),
      y: Math.round(newY),
    });
  };

  const handleResizeEnd = () => {
    resizingIdRef.current = null;
    resizeHandleRef.current = null;
    setResizingId(null);
    setResizeHandle(null);
    
    // إزالة Event Listeners بأمان
    try {
      document.removeEventListener('pointermove', handleResizeMove as any);
      document.removeEventListener('pointerup', handleResizeEnd as any);
    } catch (error) {
      console.warn('Error removing resize event listeners:', error);
    }
  };

  // Drag & Drop للصور
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    
    if (imageFiles.length === 0) return;
    
    // حساب موضع الإفلات في الكانفاس
    const canvasRect = document.getElementById('main-canvas-area')?.getBoundingClientRect();
    if (!canvasRect) return;
    
    const dropX = Math.round((e.clientX - canvasRect.left) / scale);
    const dropY = Math.round((e.clientY - canvasRect.top) / scale);
    
    // معالجة كل صورة
    for (const file of imageFiles) {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        const dataUrl = event.target?.result as string;
        
        // إنشاء صورة مؤقتة للحصول على الأبعاد
        const img = new Image();
        img.onload = () => {
          // احسب الحجم المناسب (لا يتجاوز حجم الكانفاس)
          let imgWidth = img.width;
          let imgHeight = img.height;
          
          const maxWidth = canvasWidth * 0.8; // 80% من عرض الكانفاس
          const maxHeight = canvasHeight * 0.8; // 80% من ارتفاع الكانفاس
          
          // احتفظ بالـ aspect ratio
          if (imgWidth > maxWidth || imgHeight > maxHeight) {
            const widthRatio = maxWidth / imgWidth;
            const heightRatio = maxHeight / imgHeight;
            const ratio = Math.min(widthRatio, heightRatio);
            
            imgWidth = Math.round(imgWidth * ratio);
            imgHeight = Math.round(imgHeight * ratio);
          }
          
          // ضع الصورة في موضع الإفلات (مركز الصورة = موضع الإفلات)
          const finalX = Math.max(0, dropX - imgWidth / 2);
          const finalY = Math.max(0, dropY - imgHeight / 2);
          
          // أضف الصورة كعنصر جديد
          const newImageElement: any = {
            id: `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            type: 'image' as const,
            src: dataUrl,
            x: finalX,
            y: finalY,
            width: imgWidth,
            height: imgHeight,
            objectFit: 'cover' as const,
            order: elements.length,
            fileName: file.name,
          };
          
          const updatedElements = [...elements, newImageElement];
          setElements(updatedElements as any);
          setSelectedId(newImageElement.id);
          
          showAlert({
            title: t('pages.editor.imageAdded.title'),
            message: t('pages.editor.imageAdded.message'),
            type: 'success'
          });
        };
        
        img.src = dataUrl;
      };
      
      reader.readAsDataURL(file);
    }
  };

  // 1. دالة محسنة لإضافة الكشيدة بين الحروف العربية المتصلة
  function addKashidaToLine(line: string, targetLength: number): string {
    // فقط إذا كان السطر عربي وطوله أقل من المطلوب
    if (!/[\u0600-\u06FF]/.test(line)) return line;
    if (line.length >= targetLength) return line;
    
    // الحروف العربية التي تتصل بما بعدها
    const connectingChars = /[\u0628\u062A-\u062C\u062E-\u0633\u0635-\u063A\u0641-\u064A]/;
    
    // قسم السطر إلى كلمات
    const words = line.split(' ');
    let needed = targetLength - line.length;
    
    // وزع الكشيدة بين الحروف المتصلة
    while (needed > 0) {
      let added = false;
      for (let w = 0; w < words.length; w++) {
        let word = words[w];
        // كلمة عربية أطول من 2 حروف
        if (/[\u0600-\u06FF]/.test(word) && word.length > 2) {
          // ابحث عن موضع مناسب بين حروف متصلة
          for (let i = 1; i < word.length - 1; i++) {
            const currentChar = word[i];
            const prevChar = word[i - 1];
            // تحقق إذا كان الحرف السابق يتصل والحرف الحالي ليس كشيدة
            if (connectingChars.test(prevChar) && currentChar !== 'ـ' && /[\u0600-\u06FF]/.test(currentChar)) {
              word = word.slice(0, i) + 'ـ' + word.slice(i);
              words[w] = word;
              needed--;
              added = true;
              break;
            }
          }
          if (added && needed === 0) break;
        }
      }
      if (!added) break; // لا يمكن إضافة كشيدة أكثر
    }
    return words.join(' ');
  }

  // 1.1 دالة تعتمد القياس البصري: تعبئة السطر بالكشيدة حتى يقترب من عرض الهدف بالبكسل
  function applyKashidaByWidth(
    line: string,
    targetWidthPx: number,
    measureStyles: {
      fontFamily?: string;
      fontWeight?: string | number;
      fontSizePx: number;
      letterSpacingPx?: number;
      lineHeight?: number | string;
      textTransform?: string;
      direction?: 'rtl' | 'ltr';
    }
  ): string {
    if (!/[\u0600-\u06FF]/.test(line)) return line;
    if (!targetWidthPx || targetWidthPx <= 0) return line;

    const measureSpan = document.createElement('span');
    measureSpan.style.position = 'absolute';
    measureSpan.style.visibility = 'hidden';
    measureSpan.style.whiteSpace = 'nowrap';
    measureSpan.style.fontFamily = measureStyles.fontFamily || 'Cairo, sans-serif';
    measureSpan.style.fontWeight = measureStyles.fontWeight ? String(measureStyles.fontWeight) : '400';
    measureSpan.style.fontSize = (measureStyles.fontSizePx || 16) + 'px';
    if (measureStyles.letterSpacingPx != null) measureSpan.style.letterSpacing = measureStyles.letterSpacingPx + 'px';
    if (measureStyles.lineHeight != null) measureSpan.style.lineHeight = String(measureStyles.lineHeight);
    if (measureStyles.textTransform) measureSpan.style.textTransform = measureStyles.textTransform;
    if (measureStyles.direction) measureSpan.style.direction = measureStyles.direction;

    const connectingChars = /[\u0628\u062A-\u062C\u062E-\u0633\u0635-\u063A\u0641-\u064A]/;

    let result = line;
    let safety = 0;

    const measure = (text: string) => {
      measureSpan.textContent = text;
      
      try {
        document.body.appendChild(measureSpan);
        const width = measureSpan.getBoundingClientRect().width;
        safeRemoveElement(measureSpan);
        return width;
      } catch (error) {
        console.warn('Error measuring text width:', error);
        // تنظيف العنصر في حالة الخطأ
        safeRemoveElement(measureSpan);
        return 0; // إرجاع قيمة افتراضية
      }
    };

    let currentWidth = measure(result);
    if (currentWidth >= targetWidthPx) return result;

    // جهّز مواضع صالحة لإدخال الكشيدة، وامشِ عليها بالتناوب
    const getEligiblePositions = (text: string) => {
      const positions: number[] = [];
      for (let i = 1; i < text.length - 1; i++) {
        const prev = text[i - 1];
        const cur = text[i];
        if (connectingChars.test(prev) && cur !== 'ـ' && /[\u0600-\u06FF]/.test(cur)) {
          positions.push(i);
        }
      }
      return positions;
    };

    let positions = getEligiblePositions(result);
    let posIndex = 0;

    while (currentWidth < targetWidthPx && positions.length > 0 && safety < 200) {
      const insertAt = positions[posIndex % positions.length];
      result = result.slice(0, insertAt) + 'ـ' + result.slice(insertAt);
      currentWidth = measure(result);
      posIndex++;
      safety++;
      // بعد كل بضع إدخالات، أعد حساب المواضع لتفادي التزاحم في نفس المكان
      if (posIndex % positions.length === 0) {
        positions = getEligiblePositions(result);
      }
    }

    return result;
  }

  // Reset editingProjectName when projectData changes
  React.useEffect(() => {
    setEditingProjectName(null);
  }, [projectData?.name]);

  // Mobile blocker check
  if (isMobile) {
    return <MobileBlocker />;
  }

  const handleBackClick = () => {
    if (hasUnsavedChanges) {
      setShowExitConfirm(true);
    } else {
      navigate('/dashboard');
    }
  };

  const handleSaveProjectName = async () => {
    if (!projectId || !editingProjectName || editingProjectName.trim() === '' || editingProjectName === projectData?.name) {
      setEditingProjectName(null);
      return;
    }

    try {
      const { error } = await supabase
        .from('projects')
        .update({ name: editingProjectName.trim() })
        .eq('id', projectId);

      if (error) throw error;

      // Update local projectData
      if (projectData) {
        const updatedProjectData = { ...projectData, name: editingProjectName.trim() };
        // Force re-render by updating projectData through useProjectSync
        // We'll need to reload the project or update the state
        window.location.reload(); // Simple solution - reload to get updated data
      }

      setEditingProjectName(null);
    } catch (err) {
      console.error('Error updating project name:', err);
      showAlert({
        title: t('pages.editor.saveFailed'),
        message: t('pages.editor.saveFailedMessage')
      });
    }
  };

  const confirmExit = () => {
    navigate('/dashboard');
  };

  // 🔒 Access Denied Screen
  if (accessDenied) {
    return (
      <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{
          maxWidth: 500,
          width: '100%',
          padding: 40,
          borderRadius: 16,
          background: 'linear-gradient(135deg, rgba(239,68,68,0.1), rgba(220,38,38,0.05))',
          border: '1px solid rgba(239,68,68,0.3)',
          backdropFilter: 'blur(20px)',
          textAlign: 'center'
        }}>
          {/* Icon */}
          <div style={{
            width: 80,
            height: 80,
            margin: '0 auto 24px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(239,68,68,0.2), rgba(220,38,38,0.1))',
            border: '2px solid rgba(239,68,68,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg style={{ width: 40, height: 40, color: '#ef4444' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>

          {/* Title */}
          <h1 style={{ 
            fontSize: 24, 
            fontWeight: 700, 
            color: '#fff', 
            marginBottom: 12 
          }}>
            🚫 {t('pages.editor.accessDenied.title')}
          </h1>

          {/* Message */}
          <p style={{ 
            fontSize: 15, 
            color: 'rgba(255,255,255,0.7)', 
            marginBottom: 32,
            lineHeight: 1.6
          }}>
            {t('pages.editor.accessDenied.message')}
          </p>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                padding: '12px 24px',
                borderRadius: 10,
                background: 'linear-gradient(135deg, rgba(30,72,224,0.3), rgba(59,130,246,0.22))',
                border: '1px solid rgba(30,72,224,0.6)',
                color: '#fff',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(30,72,224,0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {t('pages.editor.accessDenied.backToProjects')}
            </button>
            <button
              onClick={() => navigate('/')}
              style={{
                padding: '12px 24px',
                borderRadius: 10,
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.8)',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
              }}
            >
              {t('pages.editor.accessDenied.home')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <AlertComponent />
      
      {/* Exit Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showExitConfirm}
        onClose={() => setShowExitConfirm(false)}
        onConfirm={confirmExit}
        title={t('pages.editor.exitConfirm.title')}
        message={t('pages.editor.exitConfirm.message')}
        confirmText={t('pages.editor.exitConfirm.confirm')}
        cancelText={t('pages.editor.exitConfirm.cancel')}
        type="danger"
        icon={
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
        }
      />
      
      <div className="App" style={{ minHeight: '100vh', background: '#000000', display: 'flex', flexDirection: 'column', margin: 0, padding: 0, width: '100%', overflow: 'hidden' }}>
      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', margin: 0, padding: 0, width: '100%' }}>
        {/* Custom Header with Back Button */}
        <div style={{ 
          background: '#1a1a1a', 
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)', 
          position: 'sticky', 
          top: 0, 
          zIndex: 50,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
          width: '100%',
          margin: 0,
          padding: 0
        }} dir={dir}>
          <div style={{ 
            width: '100%', 
            margin: 0, 
            padding: '0 16px'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'flex-end', 
              height: 48,
              gap: 16
            }}>
              {/* Back Button + Title + Project Name */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0, justifyContent: 'flex-end' }}>
                <button
                  onClick={handleBackClick}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    color: '#a1a1aa',
                    background: 'transparent',
                    border: 'none',
                    padding: '6px 8px',
                    borderRadius: 4,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    fontSize: 11,
                    fontWeight: 500
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.color = '#d4d4d8';
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.color = '#a1a1aa';
                    e.currentTarget.style.background = 'transparent';
                  }}
                  title={t('pages.editor.backToProjects')}
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
                  </svg>
                  <span style={{ whiteSpace: 'nowrap' }}>{t('common.back')}</span>
                </button>
                
                <div style={{ height: 20, width: 1, background: 'rgba(255, 255, 255, 0.1)' }}></div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, justifyContent: 'flex-end' }}>
                  <span style={{ 
                    color: '#d4d4d8', 
                    fontSize: 12, 
                    fontWeight: 600, 
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    whiteSpace: 'nowrap'
                  }}>
                    {t('pages.editor.title')}
                  </span>
                  {hasUnsavedChanges && (
                    <span style={{
                      background: 'rgba(251, 146, 60, 0.15)',
                      color: '#fb923c',
                      fontSize: 9,
                      padding: '2px 6px',
                      borderRadius: 2,
                      border: '1px solid rgba(251, 146, 60, 0.3)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontWeight: 600,
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                      whiteSpace: 'nowrap'
                    }}>
                      {t('pages.editor.unsaved')}
                    </span>
                  )}
                  {projectData?.name && (
                    <>
                      <div style={{ height: 20, width: 1, background: 'rgba(255, 255, 255, 0.1)' }}></div>
                      <span style={{ 
                        color: '#71717a', 
                        fontSize: 11, 
                        fontWeight: 500,
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 200
                      }}>
                        {projectData.name}
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Save Button */}
              <button
                onClick={async () => {
                  const hasImagesToUpload = elements.some((el: any) => 
                    el.type === 'image' && 
                    el.src && 
                    !el.src.includes('supabase.co') && 
                    !el.src.startsWith('/') &&
                    !el.src.startsWith('./') &&
                    !el.src.startsWith('../')
                  );
                  
                  if (hasImagesToUpload) {
                    console.log('⏳ رفع الصور إلى Supabase...');
                  }
                  
                  const result = await saveProject(
                    elements as EditorElement[],
                    canvasWidth,
                    canvasHeight,
                    projectData?.name || 'project'
                  );
                  if (result.success) {
                    // Success notification already shown by saveSuccess state
                  } else {
                    showAlert({
                      title: t('pages.editor.saveFailed'),
                      message: result.error || t('pages.editor.unexpectedError'),
                      type: 'error'
                    });
                  }
                }}
                disabled={saving}
                style={{
                  background: saving ? '#404040' : '#0d7377',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 2,
                  padding: '6px 12px',
                  fontWeight: 500,
                  fontSize: 10,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  transition: 'all 0.15s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  flexShrink: 0,
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={e => { if (!saving) e.currentTarget.style.background = '#0a5d61'; }}
                onMouseLeave={e => { if (!saving) e.currentTarget.style.background = '#0d7377'; }}
              >
                {saving ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24" style={{ width: 12, height: 12 }}>
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>
                      {elements.some((el: any) => el.type === 'image' && el.src && !el.src.includes('supabase.co') && !el.src.startsWith('/') && !el.src.startsWith('./') && !el.src.startsWith('../')) 
                        ? t('pages.editor.savingWithImages') 
                        : t('pages.editor.saving')}
                    </span>
                  </>
                ) : (
                  <>
                    <svg style={{ width: 12, height: 12 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                    </svg>
                    <span>{t('pages.editor.saveProject')}</span>
                  </>
                )}
              </button>

              {/* Save Status */}
              {saveSuccess && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 6, 
                  color: '#4ade80', 
                  fontSize: 10,
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  fontWeight: 500,
                  flexShrink: 0
                }}>
                  <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{t('pages.editor.saved')}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 0, justifyContent: 'flex-start', alignItems: 'flex-start', padding: 0, margin: 0, width: '100%' }}>
          {/* Sidebar: الأدوات */}
          <div style={{ width: 340, height: 'calc(100vh - 48px)', background: '#1a1a1a', borderRadius: 0, padding: 0, overflowY: 'auto', position: 'sticky', top: 48, alignSelf: 'flex-start', borderRight: '1px solid rgba(255, 255, 255, 0.08)', display: 'flex', flexDirection: 'column', zIndex: 10, boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.05)', flexShrink: 0 }}>
            {/* Project Settings Header */}
            <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', background: '#1e1e1e' }}>
              <h2 style={{ fontWeight: 600, fontSize: 11, color: '#d4d4d8', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>{t('pages.editor.projectSettings')}</h2>
            </div>
            
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontWeight: 500, color: '#a1a1aa', marginBottom: 4, display: 'block', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>Project ID</label>
              <div style={{ 
                width: '100%', 
                borderRadius: 2, 
                border: '1px solid rgba(255, 255, 255, 0.1)', 
                padding: '6px 8px', 
                fontFamily: 'monospace', 
                fontWeight: 400, 
                fontSize: 10, 
                background: '#0a0a0a', 
                color: '#71717a',
                wordBreak: 'break-all',
                userSelect: 'all',
                cursor: 'text'
              }}>
                {projectId || 'N/A'}
              </div>
            </div>
            
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontWeight: 500, color: '#a1a1aa', marginBottom: 4, display: 'block', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>{t('pages.editor.projectName')}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input 
                  value={editingProjectName !== null ? editingProjectName : (projectData?.name || '')} 
                  onChange={e => setEditingProjectName(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleSaveProjectName();
                    } else if (e.key === 'Escape') {
                      setEditingProjectName(null);
                    }
                  }}
                  style={{ flex: 1, borderRadius: 2, border: '1px solid rgba(255, 255, 255, 0.1)', padding: '6px 8px', fontWeight: 400, fontSize: 11, background: '#0a0a0a', color: editingProjectName !== null ? '#d4d4d8' : '#71717a', cursor: 'text', fontFamily: 'system-ui, -apple-system, sans-serif' }} 
                />
                {editingProjectName !== null && editingProjectName !== projectData?.name && (
                  <button
                    onClick={handleSaveProjectName}
                    style={{
                      background: '#0d7377',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 2,
                      padding: '6px 8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.15s',
                      flexShrink: 0
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#0a5d61'}
                    onMouseLeave={e => e.currentTarget.style.background = '#0d7377'}
                    title={t('pages.editor.saveProjectName')}
                  >
                    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            </div>
            
            {/* Divider */}
            <div style={{ height: 1, background: 'rgba(255, 255, 255, 0.08)', margin: '8px 0' }}></div>
            
            {/* Added Elements Header */}
            <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', background: '#1e1e1e' }}>
              <h2 style={{ fontWeight: 600, fontSize: 11, color: '#d4d4d8', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>{t('pages.editor.addedElements')}</h2>
            </div>
            
            {/* Elements List */}
            <div style={{ padding: '8px', flex: 1, overflowY: 'auto' }}>
              <ElementsList selectedId={selectedId} setSelectedId={setSelectedId} />
            </div>
          </div>
            {/* Hidden export buttons - برمجي فقط */}
            <button
              style={{ width: '100%', margin: '8px 0', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 0', fontWeight: 700, fontSize: 15, cursor: 'pointer', display: 'none' }}
              onClick={() => {
                // دالة لحساب حجم الخط التلقائي
                const calculateAutoFitFontSize = (textEl: any): number => {
                  if (!textEl.autoFitFont || !textEl.width || !textEl.height) return textEl.fontSize;
                  const measureDiv = document.createElement('div');
                  measureDiv.style.position = 'absolute';
                  measureDiv.style.visibility = 'hidden';
                  measureDiv.style.width = textEl.width + 'px';
                  measureDiv.style.height = textEl.height + 'px';
                  measureDiv.style.fontFamily = textEl.fontFamily || 'Cairo, sans-serif';
                  measureDiv.style.fontWeight = textEl.fontWeight ? String(textEl.fontWeight) : '400';
                  measureDiv.style.letterSpacing = (textEl.letterSpacing || 0) + 'px';
                  measureDiv.style.lineHeight = (textEl.lineHeight || 1.5).toString();
                  measureDiv.style.textTransform = textEl.textTransform || 'none';
                  measureDiv.style.whiteSpace = 'pre-wrap';
                  measureDiv.style.wordBreak = 'break-word';
                  measureDiv.style.padding = '4px 10px';
                  measureDiv.style.boxSizing = 'border-box';
                  measureDiv.style.textAlign = textEl.textAlign || 'right';
                  measureDiv.style.direction = textEl.direction || 'rtl';
                  
                  let testFontSize = textEl.fontSize;
                  measureDiv.style.fontSize = testFontSize + 'px';
                  measureDiv.innerText = textEl.text;
                  try {
                    document.body.appendChild(measureDiv);
                    
                    while (measureDiv.scrollHeight > measureDiv.offsetHeight && testFontSize > 8) {
                      testFontSize -= 0.5;
                      measureDiv.style.fontSize = testFontSize + 'px';
                    }
                    
                    safeRemoveElement(measureDiv);
                    return testFontSize;
                  } catch (error) {
                    console.warn('Error in export font size calculation:', error);
                    // تنظيف العنصر في حالة الخطأ
                    safeRemoveElement(measureDiv);
                    return testFontSize; // إرجاع الحجم الحالي
                  }
                };
                let textIdx = 1;
                let imgIdx = 1;
                let shapeIdx = 1;
                const containerStyle = `position:relative;width:${canvasWidth}px;height:${canvasHeight}px;background:#fff;overflow:hidden;`;
                let childrenHtml = '';
                (elements as EditorElement[]).forEach((el: any) => {
                  if (el.type === 'text') {
                    const key = `text${textIdx++}`;
                    // استخدام حجم الخط الأصلي بدون تعديل لضمان دقة 100%
                    const style = [
                      `position:absolute`,
                      `left:${el.x}px`,
                      `top:${el.y}px`,
                      `width:${(el.width || 300)}px`,
                      `height:${(el.height || 80)}px`,
                      `font-size:${el.fontSize || 16}px`,
                      `color:${el.color}`,
                      `font-family:${el.fontFamily || 'Cairo, sans-serif'}`,
                      `font-weight:${el.fontWeight || 400}`,
                      `text-align:${el.textAlign || 'right'}`,
                      `direction:${el.direction || 'rtl'}`,
                      `letter-spacing:${el.letterSpacing || 0}px`,
                      `line-height:${el.lineHeight || 1.5}`,
                      `text-transform:${el.textTransform || 'none'}`,
                      `background:${el.backgroundColor || 'transparent'}`,
                      `text-shadow:${el.textShadow || 'none'}`,
                      `border-radius:6px`,
                      `padding:4px 10px`,
                      `box-sizing:border-box`,
                      `min-width:32px`,
                      `min-height:24px`,
                      `display:block`,
                      `overflow:hidden`,
                      `word-break:break-word`,
                      `white-space:pre-wrap`,
                      `user-select:text`,
                    ].join(';');
                    childrenHtml += `<div data-key="${key}" style="${style}">{{${key}}}</div>`;
                  }
                  if (el.type === 'image') {
                    const key = `img${imgIdx++}`;
                    const style = [
                      `position:absolute`,
                      `left:${el.x}px`,
                      `top:${el.y}px`,
                      `width:${el.width}px`,
                      `height:${el.height}px`,
                      `border-radius:4px`,
                      `user-select:none`,
                      `object-fit:${el.objectFit || 'cover'}`,
                    ].join(';');
                    childrenHtml += `<img data-key="${key}" src="{{${key}}}" alt="img" style="${style}" />`;
                  }
                  if (el.type === 'shape') {
                    const name = el.name || `shape-${shapeIdx++}`;
                    let shapeStyle = [
                      `position:absolute`,
                      `left:${el.x}px`,
                      `top:${el.y}px`,
                      `width:${el.width}px`,
                      `height:${el.height}px`,
                      `background:${el.color}`,
                    ];
                    
                    if (el.shapeType === 'circle') {
                      shapeStyle.push(`border-radius:50%`);
                    } else if (el.shapeType === 'rectangle' && el.borderRadius) {
                      shapeStyle.push(`border-radius:${el.borderRadius}px`);
                    } else if (el.shapeType === 'triangle') {
                      shapeStyle = [
                        `position:absolute`,
                        `left:${el.x}px`,
                        `top:${el.y}px`,
                        `width:0`,
                        `height:0`,
                        `border-left:${el.width / 2}px solid transparent`,
                        `border-right:${el.width / 2}px solid transparent`,
                        `border-bottom:${el.height}px solid ${el.color}`,
                        `border-top:none`,
                      ];
                    }
                    
                    const style = shapeStyle.join(';');
                    childrenHtml += `<div data-key="${name}" style="${style}"></div>`;
                  }
                });
                const html = `<!DOCTYPE html><html lang="ar" dir="rtl"><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width, initial-scale=1"/><title>${projectData?.name || 'Template'}</title><link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin><link href="https://fonts.googleapis.com/css2?family=Cairo:wght@300;400;700&family=Amiri:wght@400;700&family=Tajawal:wght@300;400;700&family=Noto+Naskh+Arabic:wght@400;700&family=Changa:wght@300;400;700&family=Almarai:wght@300;400;700&family=Reem+Kufi:wght@400;700&family=Lalezar&family=Lateef:wght@400;700&family=Noto+Sans+Arabic:wght@300;400;700&display=swap" rel="stylesheet"><style>html,body{margin:0;padding:0;background:#f7f7f7}#canvas{${containerStyle}}</style></head><body><div id="canvas">${childrenHtml}</div><!-- Placeholders like {{text1}}, {{img1}} --></body></html>`;
                const blob = new Blob([html], { type: 'text/html' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${projectData?.name || 'template'}.html`;
                a.click();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
              }}
            >
              {t('pages.editor.exportTemplate')}
            </button>
          {/* Main Canvas: المعاينة */}
          <div style={{ flex: 1, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(26, 26, 26, 0.5)', borderRadius: 12, position: 'relative', overflow: 'auto' }}>
            {/* Zoom Controls */}
            <div style={{ position: 'absolute', top: 20, right: 20, zIndex: 100, display: 'flex', gap: 8, background: 'rgba(26, 26, 26, 0.9)', padding: 8, borderRadius: 8, border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <button
                onClick={() => setZoomLevel(Math.min(2, zoomLevel + 0.1))}
                disabled={zoomLevel >= 2}
                style={{ background: '#1e48e0', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 12px', cursor: zoomLevel >= 2 ? 'not-allowed' : 'pointer', opacity: zoomLevel >= 2 ? 0.5 : 1, fontWeight: 'bold', fontSize: 16 }}
                title={t('pages.editor.zoomIn')}
              >
                +
              </button>
              <span style={{ color: '#fff', padding: '8px 12px', fontSize: 14, minWidth: 60, textAlign: 'center' }}>
                {Math.round(zoomLevel * 100)}%
              </span>
              <button
                onClick={() => setZoomLevel(Math.max(0.5, zoomLevel - 0.1))}
                disabled={zoomLevel <= 0.5}
                style={{ background: '#1e48e0', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 12px', cursor: zoomLevel <= 0.5 ? 'not-allowed' : 'pointer', opacity: zoomLevel <= 0.5 ? 0.5 : 1, fontWeight: 'bold', fontSize: 16 }}
                title={t('pages.editor.zoomOut')}
              >
                −
              </button>
              <button
                onClick={() => setZoomLevel(1)}
                style={{ background: 'rgba(255, 255, 255, 0.1)', color: '#fff', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 6, padding: '8px 12px', cursor: 'pointer', fontSize: 12 }}
                title={t('pages.editor.resetZoom')}
              >
                {t('pages.editor.reset')}
              </button>
            </div>
            {/* نافذة إضافة عنصر */}
            {(activeTool === 'text' || activeTool === 'image' || activeTool === 'shape') && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                background: 'rgba(0,0,0,0.7)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <div style={{ background: 'rgba(26, 26, 26, 0.95)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: 16, boxShadow: '0 8px 32px rgba(30, 72, 224, 0.2)', padding: 24, minWidth: 340, color: '#fff' }}>
                  {activeTool === 'text' && <AddTextElement />}
                  {activeTool === 'image' && <AddImageElement />}
                  {activeTool === 'shape' && selectedShapeType && <AddShapeElement shapeType={selectedShapeType} onClose={() => { setActiveTool(null); setSelectedShapeType(null); }} />}
                  <button onClick={() => { setActiveTool(null); setSelectedShapeType(null); }} style={{ marginTop: 12, width: '100%', background: '#1e48e0', color: '#fff', border: 'none', borderRadius: 8, padding: '10px', fontWeight: 700, cursor: 'pointer', transition: 'background 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#163aa8'} onMouseLeave={e => e.currentTarget.style.background = '#1e48e0'}>{t('common.close')}</button>
                </div>
              </div>
            )}
            <div
                id="main-canvas-area"
                style={{
                  width: canvasWidth * scale,
                  height: canvasHeight * scale,
                  background: '#fff',
                  // ألغِ خط الموقع داخل معاينة الكانفس لمنع الوراثة
                  fontFamily: 'initial',
                  borderRadius: 8,
                  position: 'relative',
                  overflow: 'visible', // السماح بإظهار العناصر خارج الحدود (Artboard style)
                  boxShadow: '0 2px 8px #0002',
                  userSelect: 'none',
                  pointerEvents: (activeTool === 'text' || activeTool === 'image' || activeTool === 'shape') ? 'none' : 'auto',
                  display: 'block',
                  margin: 0,
                }}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
              {/* رسم العناصر - الأحدث فوق الأقدم (z-index) */}
              {elements.map((el: any, index: number) => {
                const elementZIndex = index; // العنصر الأخير له z-index أكبر
                if (el.type === 'text') {
                  const isSelected = selectedId === el.id;
                  const isDragging = draggingId === el.id;
                  const isEditing = editingTextId === el.id;
                  let fontSize = el.fontSize * scale;
                  let overflow: 'visible' | 'hidden' | 'scroll' | 'auto' = 'visible';
                  let showEllipsis = false;
                  let style: React.CSSProperties = {};
                  let displayText = el.text;
                  
                  // استخدم حجم الخط المحسوب إذا كان موجوداً
                  if (el.width && el.height && autoFontSizes[el.id]) {
                    fontSize = autoFontSizes[el.id];
                    overflow = 'hidden';
                    
                    // تحقق إذا كان النص لا يزال لا يتسع حتى بعد التصغير
                    if (fontSize <= 8) {
                      const measureDiv = document.createElement('div');
                      measureDiv.style.position = 'absolute';
                      measureDiv.style.visibility = 'hidden';
                      measureDiv.style.width = (el.width * scale) + 'px';
                      measureDiv.style.height = (el.height * scale) + 'px';
                      measureDiv.style.fontFamily = el.fontFamily || 'Cairo, sans-serif';
                      measureDiv.style.fontWeight = el.fontWeight ? String(el.fontWeight) : '400';
                      measureDiv.style.letterSpacing = (el.letterSpacing || 0) * scale + 'px';
                      measureDiv.style.lineHeight = (el.lineHeight || 1.5).toString();
                      measureDiv.style.textTransform = el.textTransform || 'none';
                      measureDiv.style.whiteSpace = 'pre-wrap';
                      measureDiv.style.wordBreak = 'break-word';
                      measureDiv.style.padding = '4px 10px';
                      measureDiv.style.boxSizing = 'border-box';
                      measureDiv.style.fontSize = fontSize + 'px';
                      measureDiv.innerText = el.text;
                      
                      try {
                        document.body.appendChild(measureDiv);
                        const fits = measureDiv.scrollHeight <= measureDiv.offsetHeight;
                        safeRemoveElement(measureDiv);
                        if (!fits) showEllipsis = true;
                      } catch (error) {
                        console.warn('Error measuring text:', error);
                        // تنظيف العنصر في حالة الخطأ
                        safeRemoveElement(measureDiv);
                      }
                    }
                  }
                  // تطبيق الكشيدة باستخدام CSS
                  if (el.kashidaAlign === 'justify') {
                    style.textAlign = 'justify';
                    style.textAlignLast = el.textAlign || 'right'; // محاذاة السطر الأخير
                    style.textJustify = 'inter-word'; // مسافات بين الكلمات
                  }
                  const appliedFont = el.fontFamily || 'Cairo, sans-serif';
                  
                  return (
                    <React.Fragment key={el.id}>
                    <div
                      data-el-id={el.id}
                      ref={(node) => {
                        if (node) {
                          node.style.setProperty('font-family', appliedFont, 'important');
                        }
                        if (isEditing) {
                          editingTextRef.current = node;
                        }
                      }}
                      contentEditable={isEditing}
                      suppressContentEditableWarning={true}
                      onBlur={() => { if (isEditing) handleBlurEditedText(el); }}
                      onKeyDown={(e) => {
                        if (!isEditing) return;
                        
                        // Shift+Enter: إدراج سطر جديد (السلوك الافتراضي)
                        if (e.key === 'Enter' && e.shiftKey) {
                          // السماح بالسلوك الافتراضي
                          return;
                        }
                        
                        // Enter: حفظ وإغلاق التحرير
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSaveEditedText(el);
                          return;
                        }
                        
                        // Escape: إلغاء التحرير
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          setEditingTextId(null);
                          return;
                        }
                      }}
                      style={{
                        position: 'absolute',
                        left: el.x * scale,
                        top: el.y * scale,
                        width: (el.width || 300) * scale,
                        height: (el.height || 80) * scale,
                        fontSize,
                        color: el.color,
                        fontWeight: el.fontWeight || 400,
                        textAlign: style.textAlign || el.textAlign || 'right',
                        textAlignLast: style.textAlignLast || undefined,
                        textJustify: style.textJustify || undefined,
                        direction: el.direction || 'rtl',
                        letterSpacing: (el.letterSpacing || 0) * scale,
                        lineHeight: el.lineHeight || 1.5,
                        textTransform: el.textTransform || 'none',
                        background: el.backgroundColor || 'transparent',
                        textShadow: el.textShadow || 'none',
                        cursor: isEditing ? 'text' : (el.locked ? 'not-allowed' : (isDragging ? 'grabbing' : 'grab')),
                        border: isEditing ? '2px solid rgba(16, 185, 129, 0.3)' : (isSelected ? (isDragging ? '2px solid #2563eb' : '2px solid #3b82f6') : 'none'),
                        backgroundColor: isEditing ? 'rgba(236, 253, 245, 0.3)' : (showEllipsis ? '#fee2e2' : (isSelected ? '#e0e7ff33' : el.backgroundColor || 'transparent')),
                        borderRadius: 6,
                        padding: '4px 10px',
                        zIndex: isDragging ? 9999 : elementZIndex,
                        transition: isDragging ? 'none' : 'box-shadow 0.2s',
                        opacity: el.hideInRunner ? 0.5 : 1,
                        outline: el.hideInRunner ? '2px dashed #ef4444' : 'none',
                        outlineOffset: '-2px',
                        boxShadow: isDragging ? '0 4px 16px #2563eb33' : 'none',
                        minWidth: 32,
                        minHeight: 24,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: el.verticalAlign === 'middle' ? 'center' : el.verticalAlign === 'bottom' ? 'flex-end' : 'flex-start',
                        // لا نخلط بين overflow المختصر و overflowX/overflowY
                        overflow: el.autoFitFont ? 'hidden' : overflow,
                        userSelect: isEditing ? 'text' : (isDragging && draggingId === el.id ? 'none' : 'text'),
                        wordBreak: 'break-word',
                        whiteSpace: isEditing ? 'pre-wrap' : undefined, // الحفاظ على الأسطر الجديدة عند التحرير
                        resize: 'none',
                        touchAction: isEditing ? 'auto' : 'none', // السماح بالتفاعل عند التحرير
                      }}
                      onPointerDown={e => { if (!isEditing) handlePointerDownOnElement(el, e); }}
                      onClick={e => { if (!isDraggingRef.current && !isEditing) setSelectedId(el.id); }}
                      onDoubleClick={(e) => handleDoubleClickText(el, e)}
                    >
                      {isEditing ? (
                        displayText
                      ) : showEllipsis ? (
                        <span style={{ direction: 'inherit', fontSize, color: '#b91c1c', fontFamily: 'inherit' }}>{displayText} ...</span>
                      ) : (
                        displayText.split('\n').map((line: string, idx: number) => <div key={idx} style={{ fontFamily: 'inherit' }}>{line}</div>)
                      )}
                      {/* إظهار قيمة الموضع أثناء السحب */}
                      {isDragging && !isEditing && (
                        <span style={{
                          position: 'absolute',
                          left: '100%',
                          top: 0,
                          marginRight: 8,
                          background: '#2563eb',
                          color: '#fff',
                          fontSize: 13,
                          borderRadius: 6,
                          padding: '2px 8px',
                          marginLeft: 8,
                          boxShadow: '0 2px 8px #0002',
                          whiteSpace: 'nowrap',
                        }}>
                          X: {Math.round(el.x)} | Y: {Math.round(el.y)}
                        </span>
                      )}
                    </div>
                    
                    {/* Resize Handles للنص - 8 نقاط تحكم */}
                    {isSelected && !el.locked && !isDragging && !isEditing && (
                      <>
                        <div style={{ position: 'absolute', left: el.x * scale - 5, top: el.y * scale - 5, width: 10, height: 10, background: '#3b82f6', border: '2px solid #fff', borderRadius: '50%', cursor: 'nwse-resize', zIndex: 100, pointerEvents: 'auto', boxShadow: '0 0 4px rgba(0,0,0,0.3)' }} onPointerDown={e => handleResizeStart(el, 'nw', e)} />
                        <div style={{ position: 'absolute', left: el.x * scale + (el.width || 300) * scale - 5, top: el.y * scale - 5, width: 10, height: 10, background: '#3b82f6', border: '2px solid #fff', borderRadius: '50%', cursor: 'nesw-resize', zIndex: 100, pointerEvents: 'auto', boxShadow: '0 0 4px rgba(0,0,0,0.3)' }} onPointerDown={e => handleResizeStart(el, 'ne', e)} />
                        <div style={{ position: 'absolute', left: el.x * scale + (el.width || 300) * scale - 5, top: el.y * scale + (el.height || 80) * scale - 5, width: 10, height: 10, background: '#3b82f6', border: '2px solid #fff', borderRadius: '50%', cursor: 'nwse-resize', zIndex: 100, pointerEvents: 'auto', boxShadow: '0 0 4px rgba(0,0,0,0.3)' }} onPointerDown={e => handleResizeStart(el, 'se', e)} />
                        <div style={{ position: 'absolute', left: el.x * scale - 5, top: el.y * scale + (el.height || 80) * scale - 5, width: 10, height: 10, background: '#3b82f6', border: '2px solid #fff', borderRadius: '50%', cursor: 'nesw-resize', zIndex: 100, pointerEvents: 'auto', boxShadow: '0 0 4px rgba(0,0,0,0.3)' }} onPointerDown={e => handleResizeStart(el, 'sw', e)} />
                        <div style={{ position: 'absolute', left: el.x * scale + ((el.width || 300) * scale) / 2 - 5, top: el.y * scale - 5, width: 10, height: 10, background: '#3b82f6', border: '2px solid #fff', borderRadius: '50%', cursor: 'ns-resize', zIndex: 100, pointerEvents: 'auto', boxShadow: '0 0 4px rgba(0,0,0,0.3)' }} onPointerDown={e => handleResizeStart(el, 'n', e)} />
                        <div style={{ position: 'absolute', left: el.x * scale + ((el.width || 300) * scale) / 2 - 5, top: el.y * scale + (el.height || 80) * scale - 5, width: 10, height: 10, background: '#3b82f6', border: '2px solid #fff', borderRadius: '50%', cursor: 'ns-resize', zIndex: 100, pointerEvents: 'auto', boxShadow: '0 0 4px rgba(0,0,0,0.3)' }} onPointerDown={e => handleResizeStart(el, 's', e)} />
                        <div style={{ position: 'absolute', left: el.x * scale + (el.width || 300) * scale - 5, top: el.y * scale + ((el.height || 80) * scale) / 2 - 5, width: 10, height: 10, background: '#3b82f6', border: '2px solid #fff', borderRadius: '50%', cursor: 'ew-resize', zIndex: 100, pointerEvents: 'auto', boxShadow: '0 0 4px rgba(0,0,0,0.3)' }} onPointerDown={e => handleResizeStart(el, 'e', e)} />
                        <div style={{ position: 'absolute', left: el.x * scale - 5, top: el.y * scale + ((el.height || 80) * scale) / 2 - 5, width: 10, height: 10, background: '#3b82f6', border: '2px solid #fff', borderRadius: '50%', cursor: 'ew-resize', zIndex: 100, pointerEvents: 'auto', boxShadow: '0 0 4px rgba(0,0,0,0.3)' }} onPointerDown={e => handleResizeStart(el, 'w', e)} />
                      </>
                    )}
                    </React.Fragment>
                  );
                }
                if (el.type === 'image') {
                  const isSelected = selectedId === el.id;
                  const isDragging = draggingId === el.id;
                  const isResizing = resizingId === el.id;
                  
                  return (
                    <React.Fragment key={el.id}>
                      <img
                        data-el-id={el.id}
                        src={el.src}
                        alt="img"
                        style={{
                          position: 'absolute',
                          left: el.x * scale,
                          top: el.y * scale,
                          width: el.width * scale,
                          height: el.height * scale,
                          cursor: el.locked ? 'not-allowed' : (isDragging ? 'grabbing' : 'grab'),
                          border: isSelected ? (isDragging || isResizing ? '2px solid #2563eb' : '1px solid #3b82f6') : 'none',
                        background: isSelected ? '#e0e7ff55' : 'transparent',
                        borderRadius: 4,
                        userSelect: 'none',
                        zIndex: isDragging || isResizing ? 9999 : elementZIndex,
                          transition: isDragging || isResizing ? 'none' : 'box-shadow 0.2s',
                          boxShadow: isDragging || isResizing ? '0 4px 16px #2563eb33' : 'none',
                          objectFit: el.objectFit || 'cover',
                          outline: el.hideInRunner ? '2px dashed #ef4444' : 'none',
                          outlineOffset: '-2px',
                          touchAction: 'none', // منع scroll أثناء السحب على الموبايل
                          pointerEvents: 'auto', // تأكد من استقبال الأحداث
                          opacity: 1,
                        }}
                        onPointerDown={e => handlePointerDownOnElement(el, e)}
                        onClick={e => { if (!isDraggingRef.current) setSelectedId(el.id); }}
                      />
                      
                      {/* Resize Handles - فقط للصورة المحددة */}
                      {isSelected && !el.locked && !isDragging && (
                        <>
                          {/* الزوايا الأربعة */}
                          {/* نقطة شمال غرب (top-left) */}
                          <div
                            style={{
                              position: 'absolute',
                              left: el.x * scale - 4,
                              top: el.y * scale - 4,
                              width: 8,
                              height: 8,
                              background: '#3b82f6',
                              border: '2px solid #fff',
                              borderRadius: '50%',
                              cursor: 'nwse-resize',
                              zIndex: 100,
                              pointerEvents: 'auto',
                              boxShadow: '0 0 4px rgba(0,0,0,0.3)',
                            }}
                            onPointerDown={e => handleResizeStart(el, 'nw', e)}
                          />
                          {/* نقطة شمال شرق (top-right) */}
                          <div
                            style={{
                              position: 'absolute',
                              left: el.x * scale + el.width * scale - 4,
                              top: el.y * scale - 4,
                              width: 8,
                              height: 8,
                              background: '#3b82f6',
                              border: '2px solid #fff',
                              borderRadius: '50%',
                              cursor: 'nesw-resize',
                              zIndex: 100,
                              pointerEvents: 'auto',
                              boxShadow: '0 0 4px rgba(0,0,0,0.3)',
                            }}
                            onPointerDown={e => handleResizeStart(el, 'ne', e)}
                          />
                          {/* نقطة جنوب شرق (bottom-right) */}
                          <div
                            style={{
                              position: 'absolute',
                              left: el.x * scale + el.width * scale - 4,
                              top: el.y * scale + el.height * scale - 4,
                              width: 8,
                              height: 8,
                              background: '#3b82f6',
                              border: '2px solid #fff',
                              borderRadius: '50%',
                              cursor: 'nwse-resize',
                              zIndex: 100,
                              pointerEvents: 'auto',
                              boxShadow: '0 0 4px rgba(0,0,0,0.3)',
                            }}
                            onPointerDown={e => handleResizeStart(el, 'se', e)}
                          />
                          {/* نقطة جنوب غرب (bottom-left) */}
                          <div
                            style={{
                              position: 'absolute',
                              left: el.x * scale - 4,
                              top: el.y * scale + el.height * scale - 4,
                              width: 8,
                              height: 8,
                              background: '#3b82f6',
                              border: '2px solid #fff',
                              borderRadius: '50%',
                              cursor: 'nesw-resize',
                              zIndex: 100,
                              pointerEvents: 'auto',
                              boxShadow: '0 0 4px rgba(0,0,0,0.3)',
                            }}
                            onPointerDown={e => handleResizeStart(el, 'sw', e)}
                          />
                          
                          {/* الأضلاع الأربعة */}
                          {/* شمال (top) */}
                          <div
                            style={{
                              position: 'absolute',
                              left: el.x * scale + (el.width * scale / 2) - 4,
                              top: el.y * scale - 4,
                              width: 8,
                              height: 8,
                              background: '#3b82f6',
                              border: '2px solid #fff',
                              borderRadius: '50%',
                              cursor: 'ns-resize',
                              zIndex: 100,
                              pointerEvents: 'auto',
                              boxShadow: '0 0 4px rgba(0,0,0,0.3)',
                            }}
                            onPointerDown={e => handleResizeStart(el, 'n', e)}
                          />
                          {/* شرق (right) */}
                          <div
                            style={{
                              position: 'absolute',
                              left: el.x * scale + el.width * scale - 4,
                              top: el.y * scale + (el.height * scale / 2) - 4,
                              width: 8,
                              height: 8,
                              background: '#3b82f6',
                              border: '2px solid #fff',
                              borderRadius: '50%',
                              cursor: 'ew-resize',
                              zIndex: 100,
                              pointerEvents: 'auto',
                              boxShadow: '0 0 4px rgba(0,0,0,0.3)',
                            }}
                            onPointerDown={e => handleResizeStart(el, 'e', e)}
                          />
                          {/* جنوب (bottom) */}
                          <div
                            style={{
                              position: 'absolute',
                              left: el.x * scale + (el.width * scale / 2) - 4,
                              top: el.y * scale + el.height * scale - 4,
                              width: 8,
                              height: 8,
                              background: '#3b82f6',
                              border: '2px solid #fff',
                              borderRadius: '50%',
                              cursor: 'ns-resize',
                              zIndex: 100,
                              pointerEvents: 'auto',
                              boxShadow: '0 0 4px rgba(0,0,0,0.3)',
                            }}
                            onPointerDown={e => handleResizeStart(el, 's', e)}
                          />
                          {/* غرب (left) */}
                          <div
                            style={{
                              position: 'absolute',
                              left: el.x * scale - 4,
                              top: el.y * scale + (el.height * scale / 2) - 4,
                              width: 8,
                              height: 8,
                              background: '#3b82f6',
                              border: '2px solid #fff',
                              borderRadius: '50%',
                              cursor: 'ew-resize',
                              zIndex: 100,
                              pointerEvents: 'auto',
                              boxShadow: '0 0 4px rgba(0,0,0,0.3)',
                            }}
                            onPointerDown={e => handleResizeStart(el, 'w', e)}
                          />
                        </>
                      )}
                    </React.Fragment>
                  );
                }
                if (el.type === 'shape') {
                  const isSelected = selectedId === el.id;
                  const isDragging = draggingId === el.id;
                  
                  let shapeStyle: React.CSSProperties = {
                    position: 'absolute',
                    left: el.x * scale,
                    top: el.y * scale,
                    width: el.width * scale,
                    height: el.height * scale,
                    background: el.color,
                    zIndex: elementZIndex,
                    cursor: isDragging ? 'grabbing' : 'grab',
                    opacity: isDragging ? 0.8 : 1,
                    border: isSelected ? '2px solid #1e48e0' : 'none',
                    boxShadow: isSelected ? '0 0 0 2px rgba(30, 72, 224, 0.2)' : 'none',
                    pointerEvents: 'auto',
                    touchAction: 'none',
                  };

                  if (el.shapeType === 'circle') {
                    shapeStyle.borderRadius = '50%';
                  } else if (el.shapeType === 'rectangle') {
                    shapeStyle.borderRadius = (el.borderRadius || 0) * scale;
                  } else if (el.shapeType === 'triangle') {
                    shapeStyle.background = 'transparent';
                    shapeStyle.width = 0;
                    shapeStyle.height = 0;
                    shapeStyle.borderLeft = `${(el.width * scale) / 2}px solid transparent`;
                    shapeStyle.borderRight = `${(el.width * scale) / 2}px solid transparent`;
                    shapeStyle.borderBottom = `${el.height * scale}px solid ${el.color}`;
                    shapeStyle.borderTop = 'none';
                  }

                  return (
                    <React.Fragment key={el.id}>
                      {el.shapeType === 'triangle' ? (
                        <div
                          data-el-id={el.id}
                          style={shapeStyle}
                          onPointerDown={e => handlePointerDownOnElement(el, e)}
                          onClick={e => { if (!isDraggingRef.current) setSelectedId(el.id); }}
                        />
                      ) : (
                        <div
                          data-el-id={el.id}
                          style={shapeStyle}
                          onPointerDown={e => handlePointerDownOnElement(el, e)}
                          onClick={e => { if (!isDraggingRef.current) setSelectedId(el.id); }}
                        />
                      )}
                      
                      {/* Resize Handles */}
                      {isSelected && !el.locked && !isDragging && el.shapeType !== 'triangle' && (
                        <>
                          {/* Corners */}
                          <div style={{ position: 'absolute', left: el.x * scale - 4, top: el.y * scale - 4, width: 8, height: 8, background: '#3b82f6', border: '2px solid #fff', borderRadius: '50%', cursor: 'nwse-resize', zIndex: 100, pointerEvents: 'auto', boxShadow: '0 0 4px rgba(0,0,0,0.3)' }} onPointerDown={e => handleResizeStart(el, 'nw', e)} />
                          <div style={{ position: 'absolute', left: el.x * scale + el.width * scale - 4, top: el.y * scale - 4, width: 8, height: 8, background: '#3b82f6', border: '2px solid #fff', borderRadius: '50%', cursor: 'nesw-resize', zIndex: 100, pointerEvents: 'auto', boxShadow: '0 0 4px rgba(0,0,0,0.3)' }} onPointerDown={e => handleResizeStart(el, 'ne', e)} />
                          <div style={{ position: 'absolute', left: el.x * scale + el.width * scale - 4, top: el.y * scale + el.height * scale - 4, width: 8, height: 8, background: '#3b82f6', border: '2px solid #fff', borderRadius: '50%', cursor: 'nwse-resize', zIndex: 100, pointerEvents: 'auto', boxShadow: '0 0 4px rgba(0,0,0,0.3)' }} onPointerDown={e => handleResizeStart(el, 'se', e)} />
                          <div style={{ position: 'absolute', left: el.x * scale - 4, top: el.y * scale + el.height * scale - 4, width: 8, height: 8, background: '#3b82f6', border: '2px solid #fff', borderRadius: '50%', cursor: 'nesw-resize', zIndex: 100, pointerEvents: 'auto', boxShadow: '0 0 4px rgba(0,0,0,0.3)' }} onPointerDown={e => handleResizeStart(el, 'sw', e)} />
                          {/* Edges */}
                          <div style={{ position: 'absolute', left: el.x * scale + (el.width * scale / 2) - 4, top: el.y * scale - 4, width: 8, height: 8, background: '#3b82f6', border: '2px solid #fff', borderRadius: '50%', cursor: 'ns-resize', zIndex: 100, pointerEvents: 'auto', boxShadow: '0 0 4px rgba(0,0,0,0.3)' }} onPointerDown={e => handleResizeStart(el, 'n', e)} />
                          <div style={{ position: 'absolute', left: el.x * scale + el.width * scale - 4, top: el.y * scale + (el.height * scale / 2) - 4, width: 8, height: 8, background: '#3b82f6', border: '2px solid #fff', borderRadius: '50%', cursor: 'ew-resize', zIndex: 100, pointerEvents: 'auto', boxShadow: '0 0 4px rgba(0,0,0,0.3)' }} onPointerDown={e => handleResizeStart(el, 'e', e)} />
                          <div style={{ position: 'absolute', left: el.x * scale + (el.width * scale / 2) - 4, top: el.y * scale + el.height * scale - 4, width: 8, height: 8, background: '#3b82f6', border: '2px solid #fff', borderRadius: '50%', cursor: 'ns-resize', zIndex: 100, pointerEvents: 'auto', boxShadow: '0 0 4px rgba(0,0,0,0.3)' }} onPointerDown={e => handleResizeStart(el, 's', e)} />
                          <div style={{ position: 'absolute', left: el.x * scale - 4, top: el.y * scale + (el.height * scale / 2) - 4, width: 8, height: 8, background: '#3b82f6', border: '2px solid #fff', borderRadius: '50%', cursor: 'ew-resize', zIndex: 100, pointerEvents: 'auto', boxShadow: '0 0 4px rgba(0,0,0,0.3)' }} onPointerDown={e => handleResizeStart(el, 'w', e)} />
                        </>
                      )}
                    </React.Fragment>
                  );
                }
                return null;
              })}
              {/* رسالة فارغة */}
              {elements.length === 0 && (
                <span style={{ color: '#aaa' }}>{t('pages.editor.canvasPlaceholder')}</span>
              )}
              
              {/* Smart Guides - خطوط المحاذاة */}
              {showCenterGuideX && (
                <div style={{
                  position: 'absolute',
                  left: (canvasWidth / 2) * scale,
                  top: 0,
                  width: 1,
                  height: canvasHeight * scale,
                  backgroundColor: '#3b82f6',
                  pointerEvents: 'none',
                  zIndex: 1000,
                  boxShadow: '0 0 4px rgba(59, 130, 246, 0.8)',
                }} />
              )}
              {showCenterGuideY && (
                <div style={{
                  position: 'absolute',
                  left: 0,
                  top: (canvasHeight / 2) * scale,
                  width: canvasWidth * scale,
                  height: 1,
                  backgroundColor: '#3b82f6',
                  pointerEvents: 'none',
                  zIndex: 1000,
                  boxShadow: '0 0 4px rgba(59, 130, 246, 0.8)',
                }} />
              )}
              
              {/* Canvas Border Overlay - الحدود فوق كل العناصر */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: canvasWidth * scale,
                height: canvasHeight * scale,
                border: '2px dashed #1e48e0',
                borderRadius: 8,
                pointerEvents: 'none', // لا يتداخل مع التفاعل
                zIndex: 9999, // فوق كل شيء!
                boxSizing: 'border-box',
              }} />
              
              {/* Overlay Masks - تغميق المناطق الخارجة عن الحدود (مثل Illustrator) */}
              {/* أعلى الكانفاس */}
              <div style={{
                position: 'absolute',
                left: -9999,
                top: -9999,
                width: 99999,
                height: 9999,
                background: 'rgba(0, 0, 0, 0.5)',
                pointerEvents: 'none',
                zIndex: 10000,
              }} />
              {/* أسفل الكانفاس */}
              <div style={{
                position: 'absolute',
                left: -9999,
                top: canvasHeight * scale,
                width: 99999,
                height: 9999,
                background: 'rgba(0, 0, 0, 0.5)',
                pointerEvents: 'none',
                zIndex: 10000,
              }} />
              {/* يسار الكانفاس */}
              <div style={{
                position: 'absolute',
                left: -9999,
                top: 0,
                width: 9999,
                height: canvasHeight * scale,
                background: 'rgba(0, 0, 0, 0.5)',
                pointerEvents: 'none',
                zIndex: 10000,
              }} />
              {/* يمين الكانفاس */}
              <div style={{
                position: 'absolute',
                left: canvasWidth * scale,
                top: 0,
                width: 9999,
                height: canvasHeight * scale,
                background: 'rgba(0, 0, 0, 0.5)',
                pointerEvents: 'none',
                zIndex: 10000,
              }} />
            </div>
          </div>
          {/* إعدادات العنصر المحدد */}
          <ElementSettingsPanel selected={selectedElement} canvasWidth={canvasWidth} canvasHeight={canvasHeight} />
        </div>
      </div>
      {/* 1. أضف div مخفي للتصدير في نهاية الـreturn: */}
      <div id="export-canvas-area" style={{
        position: 'fixed',
        left: -9999,
        top: 0,
        width: canvasWidth,
        height: canvasHeight,
        background: '#fff',
        overflow: 'hidden',
        zIndex: -1,
        pointerEvents: 'none',
      }}>
        {elements.map((el: any) => {
          if (el.type === 'text') {
            let fontSize = el.fontSize;
            // استخدم حجم الخط المحسوب للتصدير أيضاً
            if (el.width && el.height && autoFontSizes[el.id]) {
              // احسب حجم الخط بدون scale للتصدير
              fontSize = autoFontSizes[el.id] / scale;
            }
            let showEllipsis = false;
            if (el.width && el.height && fontSize <= 8) {
              // تحقق هل النص فعلاً لا يتسع
              const measureDiv = document.createElement('div');
              measureDiv.style.position = 'absolute';
              measureDiv.style.visibility = 'hidden';
              measureDiv.style.width = el.width + 'px';
              measureDiv.style.height = el.height + 'px';
              measureDiv.style.fontFamily = el.fontFamily || 'Cairo, sans-serif';
              measureDiv.style.fontWeight = el.fontWeight ? String(el.fontWeight) : '400';
              measureDiv.style.letterSpacing = (el.letterSpacing || 0) + 'px';
              measureDiv.style.lineHeight = (el.lineHeight || 1.5).toString();
              measureDiv.style.textTransform = el.textTransform || 'none';
              measureDiv.style.whiteSpace = 'pre-wrap';
              measureDiv.style.wordBreak = 'break-word';
              measureDiv.style.padding = '4px 10px';
              measureDiv.style.boxSizing = 'border-box';
              measureDiv.style.fontSize = fontSize + 'px';
              measureDiv.innerText = el.text;
              
              try {
                document.body.appendChild(measureDiv);
                const fits = measureDiv.scrollHeight <= measureDiv.offsetHeight;
                safeRemoveElement(measureDiv);
                if (!fits) showEllipsis = true;
              } catch (error) {
                console.warn('Error measuring text for export:', error);
                // تنظيف العنصر في حالة الخطأ
                safeRemoveElement(measureDiv);
              }
            }
            // النص للتصدير (بدون تعديل - CSS سيتولى الكشيدة)
            let exportText = el.text;
            return (
              <div
                key={el.id}
                style={{
                  position: 'absolute',
                  left: el.x,
                  top: el.y,
                  width: el.width || 300,
                  height: el.height || 80,
                  fontSize,
                  color: el.color,
                  fontFamily: el.fontFamily || 'Cairo, sans-serif',
                  fontWeight: el.fontWeight || 400,
                  textAlign: el.textAlign || 'right',
                  direction: el.direction || 'rtl',
                  letterSpacing: el.letterSpacing || 0,
                  lineHeight: el.lineHeight || 1.5,
                  textTransform: el.textTransform || 'none',
                  background: el.backgroundColor || 'transparent',
                  textShadow: el.textShadow || 'none',
                  borderRadius: 6,
                  padding: '4px 10px',
                  minWidth: 32,
                  minHeight: 24,
                  display: 'inline-block',
                  overflow: 'hidden',
                  wordBreak: 'break-word',
                  resize: 'none',
                  userSelect: 'text',
                }}
              >
                {showEllipsis ? (
                  <span style={{ direction: 'inherit', fontSize, color: '#b91c1c' }}>{exportText} ...</span>
                ) : (
                  exportText
                )}
              </div>
            );
          }
          if (el.type === 'image') {
            return (
              <img
                key={el.id}
                src={el.src}
                alt="img"
                style={{
                  position: 'absolute',
                  left: el.x,
                  top: el.y,
                  width: el.width,
                  height: el.height,
                  borderRadius: 4,
                  userSelect: 'none',
                  minWidth: 10,
                  minHeight: 10,
                  display: 'block',
                  objectFit: el.objectFit || 'cover',
                }}
              />
            );
          }
          if (el.type === 'shape') {
            let shapeStyle: React.CSSProperties = {
              position: 'absolute',
              left: el.x,
              top: el.y,
              width: el.width,
              height: el.height,
              background: el.color,
            };

            if (el.shapeType === 'circle') {
              shapeStyle.borderRadius = '50%';
            } else if (el.shapeType === 'rectangle' && el.borderRadius) {
              shapeStyle.borderRadius = el.borderRadius;
            } else if (el.shapeType === 'triangle') {
              shapeStyle.background = 'transparent';
              shapeStyle.width = 0;
              shapeStyle.height = 0;
              shapeStyle.borderLeft = `${el.width / 2}px solid transparent`;
              shapeStyle.borderRight = `${el.width / 2}px solid transparent`;
              shapeStyle.borderBottom = `${el.height}px solid ${el.color}`;
              shapeStyle.borderTop = 'none';
            }

            return (
              <div
                key={el.id}
                style={shapeStyle}
              />
            );
          }
          return null;
        })}
      </div>

      {/* Success Save Notification */}
      {saveSuccess && (
        <div 
          style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 99999,
            animation: 'slideDown 0.3s ease-out, fadeOut 0.3s ease-in 1.7s',
          }}
        >
          <div style={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: '#fff',
            padding: '16px 32px',
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(16, 185, 129, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontSize: '16px',
            fontWeight: '600',
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10" fill="rgba(255, 255, 255, 0.2)" />
              <path d="M8 12.5l2.5 2.5 5.5-5.5" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>{t('pages.editor.saveSuccess')}</span>
          </div>
        </div>
      )}
      
      {/* Toolbar at the bottom */}
      <Toolbar activeTool={activeTool} setActiveTool={setActiveTool} onShapeSelect={(shapeType) => { setSelectedShapeType(shapeType); setActiveTool('shape'); }} />

      {/* CSS Animations */}
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }
      `}</style>
    </div>
    </>
  );
}
